import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TriStateCheckbox from "../TriStateCheckbox";
import GradeInput from "../GradeInput";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  protocolsService,
  ProtocolResponse,
  ProtocolGradeResponse,
  ProtocolGradeRequest,
} from "../../services/protocolsService";
import ProtocolHeader from "./ProtocolHeader";
import toast from "react-hot-toast";
import { BiSave } from "react-icons/bi";
import { normalizeDate } from "../../common/normalizeDate";

const Protocol: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const fn = searchParams.get("fn");
  const studentName = searchParams.get("studentName");

  const { t } = useTranslation();

  const [protocolData, setProtocolData] = useState<ProtocolResponse | null>(
    null
  );
  const [grades, setGrades] = useState<ProtocolGradeResponse[]>([]);
  const [modifiedRows, setModifiedRows] = useState<Set<number>>(new Set());
  const [loadingProtocol, setLoadingProtocol] = useState<boolean>(true);
  const [loadingGrades, setLoadingGrades] = useState<boolean>(true);
  const [errorProtocol, setErrorProtocol] = useState<string | null>(null);
  const [errorGrades, setErrorGrades] = useState<string | null>(null);
  const [creatingVersion, setCreatingVersion] = useState<boolean>(false);
  const [checkingSignature, setCheckingSignature] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const gradeInputRefs = useRef<{
    regular: Map<number, HTMLInputElement>;
    multicomponent: Map<number, Map<number, HTMLInputElement>>;
  }>({
    regular: new Map(),
    multicomponent: new Map(),
  });

  useEffect(() => {
    gradeInputRefs.current.regular.clear();
    gradeInputRefs.current.multicomponent.clear();
  }, []);

  const gradeTypeOptions: { id: number; name: string }[] = [
    { id: 1, name: t("Final") },
    { id: 2, name: t("Current") },
    { id: 3, name: t("Equivalent grade") },
  ];

  const getNextInput = (currentStudentGradeId: number, subjectId?: number) => {
    const grades = sortedGrades;
    const currentIndex = grades.findIndex(
      (g) => g.studentGradeId === currentStudentGradeId
    );
    if (currentIndex === -1) return null;

    const nextGrade = grades[currentIndex + 1];
    if (!nextGrade) return null;

    if (subjectId !== undefined) {
      const componentRefs = gradeInputRefs.current.multicomponent.get(
        nextGrade.studentGradeId
      );
      return componentRefs?.get(subjectId) || null;
    } else {
      return (
        gradeInputRefs.current.regular.get(nextGrade.studentGradeId) || null
      );
    }
  };

  const handleEnterPress = (studentGradeId: number, subjectId?: number) => {
    const nextRef = getNextInput(studentGradeId, subjectId);
    if (nextRef && nextRef.isConnected) {
      nextRef.focus();
    }
  };

  const setInputRef = (
    element: HTMLInputElement | null,
    studentGradeId: number,
    subjectId?: number
  ) => {
    if (!element) return;

    if (subjectId !== undefined) {
      if (!gradeInputRefs.current.multicomponent.has(studentGradeId)) {
        gradeInputRefs.current.multicomponent.set(studentGradeId, new Map());
      }
      gradeInputRefs.current.multicomponent
        .get(studentGradeId)
        ?.set(subjectId, element);
    } else {
      gradeInputRefs.current.regular.set(studentGradeId, element);
    }
  };

  const fetchProtocol = useCallback(async () => {
    if (!id) {
      setErrorProtocol(t("Protocol ID is missing."));
      setLoadingProtocol(false);
      return;
    }

    try {
      const protocolData = await protocolsService.getProtocolById(id);
      setProtocolData(protocolData);
      setLoadingProtocol(false);
    } catch (error: any) {
      console.error("Error fetching protocol data:", error);
      setErrorProtocol(
        error.message ||
          t("An unexpected error occurred while fetching protocol data.")
      );
      setLoadingProtocol(false);
    }
  }, [id, t]);

  const fetchGrades = useCallback(async () => {
    setLoadingGrades(true);
    if (!id) {
      setErrorGrades(t("Protocol ID is missing."));
      setLoadingGrades(false);
      return;
    }

    try {
      const gradesData = await protocolsService.getProtocolGrades(id);
      setGrades(gradesData);
      setModifiedRows(new Set());
      setLoadingGrades(false);
    } catch (error: any) {
      console.error("Error fetching grades:", error);
      setErrorGrades(
        error.message ||
          t("An unexpected error occurred while fetching grades.")
      );
      setLoadingGrades(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchProtocol();
    fetchGrades();
  }, [fetchProtocol, fetchGrades]);

  const markRowAsModified = (studentGradeId: number) => {
    setModifiedRows((prev) => {
      const newSet = new Set(prev);
      newSet.add(studentGradeId);
      return newSet;
    });
  };

  const handleGradeChange = (studentGradeId: number, newGrade: number) => {
    const updatedGrades = grades.map((grade) =>
      grade.studentGradeId === studentGradeId
        ? { ...grade, grade: newGrade }
        : grade
    );
    setGrades(updatedGrades);
    markRowAsModified(studentGradeId);
  };

  const handleAttestationChange = (
    studentGradeId: number,
    newValue: number
  ) => {
    const updatedGrades = grades.map((grade) =>
      grade.studentGradeId === studentGradeId
        ? {
            ...grade,
            attestation: newValue === 2 ? false : newValue === 0 ? null : true,
          }
        : grade
    );
    setGrades(updatedGrades);
    markRowAsModified(studentGradeId);
  };

  const handleMulticomponentGradeEdit = (
    studentGradeId: number,
    subjectId: number,
    newValue: number
  ) => {
    const updatedGrades = grades.map((grade) =>
      grade.studentGradeId === studentGradeId
        ? {
            ...grade,
            studentGradeComponents: grade.studentGradeComponents.map(
              (component) =>
                component.subjectId === subjectId
                  ? { ...component, grade: newValue }
                  : component
            ),
          }
        : grade
    );
    setGrades(updatedGrades);
    markRowAsModified(studentGradeId);
  };

  const handleGradeTypeChange = (
    studentGradeId: number,
    newGradeTypeId: number
  ) => {
    const selectedGradeType = gradeTypeOptions.find(
      (option) => option.id === newGradeTypeId
    );
    if (selectedGradeType) {
      const updatedGrades = grades.map((grade) =>
        grade.studentGradeId === studentGradeId
          ? {
              ...grade,
              studentGradeTypeId: selectedGradeType.id,
              studentGradeTypeName: selectedGradeType.name,
            }
          : grade
      );
      setGrades(updatedGrades);
      markRowAsModified(studentGradeId);
    }
  };

  const handleDateChange = (studentGradeId: number, date: Date | null) => {
    if (date) {
      date = normalizeDate(date);
    }

    const updatedGrades = grades.map((grade) =>
      grade.studentGradeId === studentGradeId
        ? { ...grade, updateDate: date || undefined }
        : grade
    );
    setGrades(updatedGrades);
    markRowAsModified(studentGradeId);
  };

  const handleThesisTitleChange = (studentGradeId: number, newTitle: string) => {
    const updatedGrades = grades.map((grade) =>
      grade.studentGradeId === studentGradeId
        ? { ...grade, thesisTitle: newTitle }
        : grade
    );
    setGrades(updatedGrades);
    markRowAsModified(studentGradeId);
  };

  const handleSaveAll = async () => {
    if (modifiedRows.size === 0) {
      toast.success(t("No changes to save."));
      return;
    }

    setIsSaving(true);
    try {
      const modifiedGrades = grades.filter((grade) =>
        modifiedRows.has(grade.studentGradeId)
      );

      await Promise.all(
        modifiedGrades.map(async (grade) => {
          try {
            const payload: ProtocolGradeRequest = {
              StudentGradeId: grade.studentGradeId,
              StudentId: grade.studentId,
              ProtocolId: grade.protocolId,
              Grade: grade.grade,
              StudentGradeTypeId: grade.studentGradeTypeId,
              StudentGradeComponents: grade.studentGradeComponents,
              Attestation: grade.attestation,
              ThesisTitle: grade.thesisTitle,
              UpdateDate: grade.updateDate,
            };

            await protocolsService.updateStudentGrade(payload);
          } catch (gradeError: any) {
            const errorMessage =
              gradeError.message || t("Failed to update grade");
            throw new Error(
              `${t("Error updating grade for student")} ${
                grade.studentName
              }: ${errorMessage}`
            );
          }
        })
      );

      await fetchGrades();
      toast.success(t("All changes saved successfully."));
    } catch (error: any) {
      console.error("Error saving grades:", error);
      toast.error(
        error.message ||
          t("An unexpected error occurred while saving the grades.")
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleExport = async (format: "pdf" | "excel") => {
    if (!id) return;

    try {
      const blob = await protocolsService.downloadWorkingProtocol(id, format);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");

      let fileName = `Protocol_${protocolData?.protocolNumber || id}`;
      fileName += format === "pdf" ? ".pdf" : ".xlsx";

      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
        window.URL.revokeObjectURL(url);
      }, 0);
    } catch (error: any) {
      console.error(`Error exporting protocol as ${format}:`, error);
      toast.error(
        error.message ||
          t(
            `An unexpected error occurred while exporting protocol as ${format}.`
          )
      );
    }
  };

  const handleExportPDF = () => handleExport("pdf");
  const handleExportExcel = () => handleExport("excel");

  const handleDownloadSignedPDF = async () => {
    if (!protocolData?.isSigned) {
      toast.error(t("Protocol is not signed yet."));
      return;
    }

    try {
      const blob = await protocolsService.downloadSignedProtocol(
        protocolData.protocolId
      );
      const url = window.URL.createObjectURL(
        new Blob([blob], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Signed_Protocol_${protocolData.protocolNumber}.pdf`
      );
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
        window.URL.revokeObjectURL(url);
      }, 0);
    } catch (error: any) {
      console.error("Error downloading signed protocol:", error);
      toast.error(
        error.message ||
          t(
            "An unexpected error occurred while downloading the signed protocol."
          )
      );
    }
  };

  const handleCreateProtocolVersion = async () => {
    if (!protocolData?.protocolId) return;

    setCreatingVersion(true);
    try {
      const result = await protocolsService.createProtocolVersion(
        protocolData.protocolId
      );
      if (result) {
        toast.success(t("Protocol version created successfully."));
        await fetchProtocol();
        await fetchGrades();
      } else {
        toast.error(t("Failed to create protocol version."));
      }
    } catch (error: any) {
      console.error("Error creating protocol version:", error);
      toast.error(
        error.message ||
          t("An unexpected error occurred while creating the protocol version.")
      );
    } finally {
      setCreatingVersion(false);
    }
  };

  const handleCheckProtocolSignature = async () => {
    if (!protocolData?.protocolId) return;

    setCheckingSignature(true);
    try {
      const result = await protocolsService.checkProtocolSignature(
        protocolData.protocolId
      );
      setProtocolData((prev) => (prev ? { ...prev, isSigned: result } : prev));
      toast.success(
        result ? t("Protocol is signed.") : t("Protocol is not signed.")
      );
    } catch (error: any) {
      console.error("Error checking protocol signature:", error);
      toast.error(
        error.message ||
          t(
            "An unexpected error occurred while checking the protocol signature."
          )
      );
    } finally {
      setCheckingSignature(false);
    }
  };

  const [sortConfig, setSortConfig] = useState<{
    key: "facultyNumber" | "studentName" | "groupName" | null;
    direction: "asc" | "desc";
  }>(() => {
    const savedSort = localStorage.getItem("protocol-sort-preference");
    return savedSort ? JSON.parse(savedSort) : { key: null, direction: "asc" };
  });

  useEffect(() => {
    if (sortConfig.key) {
      localStorage.setItem(
        "protocol-sort-preference",
        JSON.stringify(sortConfig)
      );
    }
  }, [sortConfig]);

  const sortedGrades = useMemo(() => {
    if (!sortConfig.key) return grades;

    return [...grades].sort((a, b) => {
      if (sortConfig.key === "groupName") {
        const aNum = parseInt((a[sortConfig.key] || "").replace(/\D/g, ""));
        const bNum = parseInt((b[sortConfig.key] || "").replace(/\D/g, ""));

        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortConfig.direction === "asc" ? aNum - bNum : bNum - aNum;
        }
      }
      const aVal =
        a[sortConfig.key as keyof typeof a]?.toString().toLowerCase() ?? "";
      const bVal =
        b[sortConfig.key as keyof typeof b]?.toString().toLowerCase() ?? "";

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [grades, sortConfig]);
  const handleSort = (key: "facultyNumber" | "studentName" | "groupName") => {
    setSortConfig((current) => {
      const newConfig: {
        key: "facultyNumber" | "studentName" | "groupName" | null;
        direction: "asc" | "desc";
      } = {
        key,
        direction:
          current.key === key && current.direction === "asc" ? "desc" : "asc",
      };
      return newConfig;
    });
  };

  const SortIndicator: React.FC<{ column: string }> = ({ column }) => {
    if (sortConfig.key !== column) return <span className="ml-1">↕</span>;
    return (
      <span className="ml-1">{sortConfig.direction === "asc" ? "↑" : "↓"}</span>
    );
  };

  if (loadingProtocol || loadingGrades) {
    return <p>{t("Loading...")}</p>;
  }

  if (errorProtocol || errorGrades) {
    return (
      <div>
        {errorProtocol && (
          <p className="text-red-500">
            {t("Error")}: {errorProtocol}
          </p>
        )}
        {errorGrades && (
          <p className="text-red-500">
            {t("Error")}: {errorGrades}
          </p>
        )}
      </div>
    );
  }

  if (!protocolData) {
    return <p>{t("No protocol data available.")}</p>;
  }

  const { examTypeId, isSigned, childSubjects } = protocolData;

  return (
    <div className="container mx-auto p-4 bg-white dark:bg-gray-900 dark:text-white">
      <ProtocolHeader protocolData={protocolData} />

      <div className="flex justify-end mb-4 space-x-4">
        <button
          onClick={handleSaveAll}
          disabled={isSaving || isSigned || modifiedRows.size === 0}
          className={`bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded inline-flex items-center gap-2 ${
            isSaving || isSigned || modifiedRows.size === 0
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
        >
          <BiSave className="w-5 h-5" />
          {isSaving ? t("Saving...") : t("Save All Changes")}
        </button>
      </div>

      {examTypeId !== 13 && examTypeId !== 17 && (
        <div className="overflow-x-auto bg-white">
          <table className="min-w-full border border-gray-300 dark:bg-gray-800">
            <thead>
              <tr className="bg-bodydark text-white text-left dark:bg-gray-100 dark:text-black">
                <th
                  className="py-2 px-4 border-b dark:border-gray-600 cursor-pointer hover:bg-opacity-80"
                  onClick={() => handleSort("facultyNumber")}
                >
                  {t("Faculty No.")}
                  <SortIndicator column="facultyNumber" />
                </th>
                <th
                  className="py-2 px-4 border-b dark:border-gray-600 cursor-pointer hover:bg-opacity-80"
                  onClick={() => handleSort("studentName")}
                >
                  {t("Name")}
                  <SortIndicator column="studentName" />
                </th>
                <th
                  className="py-2 px-4 border-b dark:border-gray-600 cursor-pointer hover:bg-opacity-80"
                  onClick={() => handleSort("groupName")}
                >
                  {t("Group")}
                  <SortIndicator column="groupName" />
                </th>
                {examTypeId === 14 && (
                  <th className="py-2 px-4 border-b dark:border-gray-600">
                    {t("Attestation")}
                  </th>
                )}
                {examTypeId !== 14 && (
                  <th className="py-2 px-4 border-b dark:border-gray-600">
                    {t("Grade")}
                  </th>
                )}
                <th className="py-2 px-4 border-b dark:border-gray-600">
                  {t("Grade Type")}
                </th>
                <th className="py-2 px-4 border-b dark:border-gray-600">
                  {t("Date")}
                </th>
                <th className="py-2 px-2 border-b dark:border-gray-600 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {sortedGrades.length > 0 ? (
                sortedGrades.map((grade, index) => (
                  <tr
                    key={grade.studentGradeId}
                    className={`border-b dark:border-gray-600 ${
                      fn === grade.facultyNumber ||
                      (studentName &&
                        grade.studentName &&
                        grade.studentName.includes(studentName))
                        ? "bg-yellow-100 dark:bg-yellow-900"
                        : ""
                    }`}
                  >
                    <td className="py-2 px-4 dark:text-white">
                      {grade.facultyNumber || "-"}
                    </td>
                    <td className="py-2 px-4 dark:text-white">
                      {grade.studentName || "-"}
                    </td>
                    <td className="py-2 px-4 dark:text-white">
                      {grade.groupName || ""}
                    </td>
                    {examTypeId === 14 && (
                      <td className="py-2 px-4 dark:text-white">
                        <div className="flex flex-row gap-2 w-48">
                          {!isSigned && (
                            <TriStateCheckbox
                              value={
                                grade.attestation === true
                                  ? 1
                                  : grade.attestation === false
                                  ? 2
                                  : 0
                              }
                              onStateChange={(state: number) =>
                                handleAttestationChange(
                                  grade.studentGradeId,
                                  state
                                )
                              }
                              disabled={isSigned}
                            />
                          )}
                          <div>
                            {t(
                              grade.attestation === true
                                ? "Да"
                                : grade.attestation === false
                                ? "Не"
                                : "Непопълнено"
                            )}
                          </div>
                        </div>
                      </td>
                    )}
                    {examTypeId !== 14 && (
                      <td className="py-2 px-4 dark:text-white">
                        <div className="flex items-center">
                          {!isSigned ? (
                            <GradeInput
                              value={grade.grade}
                              onChange={(value) =>
                                handleGradeChange(grade.studentGradeId, value)
                              }
                              className="w-24"
                              onEnterPress={() =>
                                handleEnterPress(grade.studentGradeId)
                              }
                              inputRef={(el) =>
                                setInputRef(el, grade.studentGradeId)
                              }
                              disabled={isSigned}
                            />
                          ) : (
                            <span className="mx-2">
                              {grade.gradeName} {grade.grade}
                            </span>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="py-2 px-4 dark:text-white">
                      <div className="flex items-center">
                        {isSigned ? (
                          <span className="mx-2">
                            {grade.studentGradeTypeName}
                          </span>
                        ) : (
                          <select
                            value={grade.studentGradeTypeId}
                            onChange={(e) =>
                              handleGradeTypeChange(
                                grade.studentGradeId,
                                parseInt(e.target.value)
                              )
                            }
                            className="bg-gray-200 border border-gray-400 rounded-md p-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            disabled={isSigned}
                          >
                            <option value="" disabled>
                              {t("Select type")}
                            </option>
                            {gradeTypeOptions.map((option) => (
                              <option
                                key={option.id}
                                value={option.id}
                                className="dark:bg-gray-800"
                              >
                                {option.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-4 dark:text-white">
                      {isSigned ? (
                        <span>
                          {grade.updateDate
                            ? new Date(grade.updateDate).toLocaleDateString()
                            : "-"}
                        </span>
                      ) : (
                        <DatePicker
                          selected={
                            grade.updateDate
                              ? normalizeDate(grade.updateDate)
                              : null
                          }
                          onChange={(date: Date | null) =>
                            handleDateChange(grade.studentGradeId, date)
                          }
                          dateFormat="dd.MM.yyyy"
                          className="bg-gray-200 border border-gray-400 rounded-md p-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          placeholderText={t("Select date")}
                          showTimeSelect={false}
                          disabled={isSigned}
                        />
                      )}
                      <div className="text-xs text-gray-400">
                        {grade.modifiedByUsername}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-center">
                      {modifiedRows.has(grade.studentGradeId) && (
                        <div className="w-4 h-4 bg-green-500 rounded-full mx-auto"></div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={examTypeId === 14 ? 7 : 7}
                    className="py-2 px-4 text-center dark:text-white"
                  >
                    {t("No grades available.")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {(examTypeId === 13 || examTypeId === 17) && (
        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-300 dark:bg-gray-800">
            <thead>
              <tr
                className={`bg-bodydark text-white text-left dark:bg-gray-100 dark:text-black`}
              >
                <th className="py-2 px-4 border-b dark:border-gray-600">
                  {t("Faculty No.")}
                </th>
                <th className="py-2 px-4 border-b dark:border-gray-600">
                  {t("Name")}
                </th>
                <th className="py-2 px-4 border-b dark:border-gray-600">
                  {t("Group")}
                </th>
                {examTypeId === 17 && (
                  <th className="py-2 px-4 border-b dark:border-gray-600">
                    {t("Thesis Title")}
                  </th>
                )}
                {childSubjects &&
                  childSubjects.map((cs) => (
                    <th
                      key={cs.subjectId}
                      className="py-2 px-4 border-b dark:border-gray-600"
                    >
                      {cs.name}
                    </th>
                  ))}
                <th className="py-2 px-4 border-b dark:border-gray-600">
                  {t("Grade")}
                </th>
                <th className="py-2 px-4 border-b dark:border-gray-600">
                  {t("Grade Type")}
                </th>
                <th className="py-2 px-4 border-b dark:border-gray-600">
                  {t("Date")}
                </th>
                <th className="py-2 px-2 border-b dark:border-gray-600 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {sortedGrades.length > 0 ? (
                sortedGrades.map((grade, index) => (
                  <tr
                    key={grade.studentGradeId}
                    className={`border-b dark:border-gray-600 ${
                      fn === grade.facultyNumber ||
                      (studentName &&
                        grade.studentName &&
                        grade.studentName.includes(studentName))
                        ? "bg-yellow-100 dark:bg-yellow-900"
                        : ""
                    }`}
                  >
                    <td className="py-2 px-4 dark:text-white">
                      {grade.facultyNumber || "-"}
                    </td>
                    <td className="py-2 px-4 dark:text-white">
                      {grade.studentName || "-"}
                    </td>
                    <td className="py-2 px-4 dark:text-white">
                      {grade.groupName || ""}
                    </td>
                    {examTypeId === 17 && (
                      <td className="py-2 px-4 dark:text-white">
                        {isSigned ? (
                          <span>{grade.thesisTitle || "-"}</span>
                        ) : (
                          <input
                            type="text"
                            value={grade.thesisTitle || ""}
                            onChange={(e) =>
                              handleThesisTitleChange(
                                grade.studentGradeId,
                                e.target.value
                              )
                            }
                            className="bg-gray-200 border border-gray-400 rounded-md p-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            disabled={isSigned}
                          />
                        )}
                      </td>
                    )}
                    {childSubjects &&
                      childSubjects.map((cs) => (
                        <td
                          key={cs.subjectId}
                          className="py-2 px-4 dark:text-white"
                        >
                          {isSigned ? (
                            <span>
                              {grade?.studentGradeComponents?.find(
                                (x) => x.subjectId === cs.subjectId
                              )?.grade || 0}
                            </span>
                          ) : (
                            <GradeInput
                              value={
                                grade?.studentGradeComponents?.find(
                                  (x) => x.subjectId === cs.subjectId
                                )?.grade || 0
                              }
                              onChange={(value) =>
                                handleMulticomponentGradeEdit(
                                  grade.studentGradeId,
                                  cs.subjectId,
                                  value
                                )
                              }
                              className="w-24"
                              onEnterPress={() =>
                                handleEnterPress(
                                  grade.studentGradeId,
                                  cs.subjectId
                                )
                              }
                              inputRef={(el) =>
                                setInputRef(
                                  el,
                                  grade.studentGradeId,
                                  cs.subjectId
                                )
                              }
                              disabled={isSigned}
                            />
                          )}
                        </td>
                      ))}

                    <td className="py-2 px-4 dark:text-white">
                      <div className="flex items-center">
                        {examTypeId === 17 && !isSigned ? (
                          <GradeInput
                            value={grade.grade}
                            onChange={(value) =>
                              handleGradeChange(grade.studentGradeId, value)
                            }
                            className="w-24"
                            onEnterPress={() =>
                              handleEnterPress(grade.studentGradeId)
                            }
                            inputRef={(el) =>
                              setInputRef(el, grade.studentGradeId)
                            }
                            disabled={isSigned}
                          />
                        ) : (
                          <span className="mx-2">
                            {grade.gradeName} {grade.grade}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-4 dark:text-white">
                      <div className="flex items-center">
                        {isSigned ? (
                          <span className="mx-2">
                            {grade.studentGradeTypeName}
                          </span>
                        ) : (
                          <select
                            value={grade.studentGradeTypeId}
                            onChange={(e) =>
                              handleGradeTypeChange(
                                grade.studentGradeId,
                                parseInt(e.target.value)
                              )
                            }
                            className="bg-gray-200 border border-gray-400 rounded-md p-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            disabled={isSigned}
                          >
                            <option value="" disabled>
                              {t("Select type")}
                            </option>
                            {gradeTypeOptions.map((option) => (
                              <option
                                key={option.id}
                                value={option.id}
                                className="dark:bg-gray-800"
                              >
                                {option.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-4 dark:text-white">
                      {isSigned ? (
                        <span>
                          {grade.updateDate
                            ? new Date(grade.updateDate).toLocaleDateString()
                            : "-"}
                        </span>
                      ) : (
                        <DatePicker
                          selected={
                            grade.updateDate
                              ? normalizeDate(grade.updateDate)
                              : null
                          }
                          onChange={(date: Date | null) =>
                            handleDateChange(grade.studentGradeId, date)
                          }
                          dateFormat="dd.MM.yyyy"
                          className="bg-gray-200 border border-gray-400 rounded-md p-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          placeholderText={t("Select date")}
                          showTimeSelect={false}
                          disabled={isSigned}
                        />
                      )}
                      <div className="text-xs text-gray-400">
                        {grade.modifiedByUsername}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-center">
                      {modifiedRows.has(grade.studentGradeId) && (
                        <div className="w-4 h-4 bg-green-500 rounded-full mx-auto"></div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={childSubjects ? childSubjects.length + (examTypeId === 17 ? 6 : 5) : (examTypeId === 17 ? 7 : 6)}
                    className="py-2 px-4 text-center dark:text-white"
                  >
                    {t("No grades available.")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-row flex-wrap justify-center mt-4 gap-4">
        <button
          onClick={handleExportPDF}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center gap-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586L7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z"
              clipRule="evenodd"
            />
          </svg>
          {t("Export to PDF")}
        </button>
        <button
          onClick={handleExportExcel}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center gap-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z"
              clipRule="evenodd"
            />
          </svg>
          {t("Export to Excel")}
        </button>

        <button
          onClick={handleCheckProtocolSignature}
          className={`bg-indigo-500 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded flex items-center gap-2 ${
            checkingSignature ? "opacity-50 cursor-not-allowed" : ""
          }`}
          disabled={checkingSignature}
          title={
            checkingSignature
              ? t("Checking signature...")
              : t("Check if protocol is signed")
          }
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          {checkingSignature ? t("Checking...") : t("Check Signature")}
        </button>

        <button
          onClick={handleDownloadSignedPDF}
          className={`bg-indigo-500 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded flex items-center gap-2 ${
            !isSigned ? "opacity-50 cursor-not-allowed" : ""
          }`}
          disabled={!isSigned}
          title={
            !isSigned
              ? t("Protocol is not signed yet.")
              : t("Download signed PDF")
          }
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
          {t("Download Signed PDF")}
        </button>
        <button
          onClick={handleCreateProtocolVersion}
          className={`bg-primary hover:bg-primary-700 text-white font-bold py-2 px-4 rounded flex items-center gap-2 ${
            creatingVersion ? "opacity-50 cursor-not-allowed" : ""
          }`}
          disabled={creatingVersion}
          title={
            creatingVersion
              ? t("Creating a new version...")
              : t("Create a new protocol version")
          }
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
              clipRule="evenodd"
            />
          </svg>
          {creatingVersion ? t("Creating...") : t("Create New Version")}
        </button>
      </div>
    </div>
  );
};

export default Protocol;
